import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  closeTheStudio,
  previewClosure,
  previewClosureMessage,
  type ClosurePreview,
} from '../api/closures'
import { formatFullDate, today } from '../api/calendar'
import Modal from './Modal'

/**
 * Tonya closes the studio, and sees what it costs before she does (D-735,
 * D-740, D-741).
 *
 * **Two taps in a storm is the design target**, so the shape is: open it, read
 * one sentence saying what goes and who hears, press the button. Everything
 * else on this sheet is optional and every field can be left alone.
 *
 * **It previews before it applies, and the preview is not decoration.** A
 * closure is the one control in the app that reaches every family at once and
 * cancels a night of classes, and neither half is undoable by pressing it
 * again — reopening is its own decision that has to tell people (D-738). So
 * the count is on the button: *Close and tell 214 people*.
 *
 * **What it does not ask.** No closure kind: every closure made here is a
 * weather closure, as the existing code already records, and a dropdown nobody
 * needs is a decision at 6am in a storm.
 */
export default function CloseTheStudioSheet({
  date,
  onDismiss,
  onClosed,
}: {
  /** The day she opened this from. The starting point, not the answer. */
  date: string
  onDismiss: () => void
  onClosed: () => void
}) {
  /*
   * <b>Two dates she can both see and change</b> (D-803).
   *
   * `asked`, 2026-09-23: *"the 'last day closed' being kind of confusing.
   * Especially because this doesn't seem to allow you to future date a
   * closure... it really should just be like booking pto in other
   * applications where you select a start date, an end date, and then maybe
   * just a toggle for all day or not."*
   *
   * <b>The start was never a field.</b> It was whichever day the calendar
   * happened to be showing, printed as a sentence under the heading, and the
   * only date input was labelled *Last day closed* with *leave it blank to
   * close this day only* underneath. So the first day was unchangeable, the
   * second was optional, and neither read as one end of a range.
   *
   * <b>Future-dating already worked and nothing said so.</b> `observed`: the
   * endpoint refuses only a day <i>before today</i>, so a closure three weeks
   * out has always been accepted — she just had to navigate the calendar there
   * first and guess that opening the sheet from that day was how you did it.
   * A capability nobody can find is the same defect as one that was never
   * built, which is the lesson the reopen button cost us four days ago (D-792).
   */
  const [from, setFrom] = useState(date)
  const [to, setTo] = useState(date)
  const [fromTime, setFromTime] = useState('')
  const [reason, setReason] = useState('')
  const [familyNote, setFamilyNote] = useState('')
  const [regularScheduleOnly, setRegularScheduleOnly] = useState(false)

  /*
   * <b>The end follows the start rather than going invalid behind her.</b>
   * Moving the first day past the last one would otherwise leave a range the
   * server silently swaps (its `Range` sorts them), so the screen would say one
   * thing and the closure would do another.
   */
  const setFirstDay = (day: string) => {
    setFrom(day)
    if (day > to) setTo(day)
  }

  const request = {
    from,
    to,
    fromTime: fromTime || null,
    reason: reason || null,
    familyNote: familyNote || null,
    regularScheduleOnly,
  }

  /*
   * Re-previewed whenever the range or the cutoff moves, because both change
   * what is called off and who is told - a count that went stale the moment she
   * picked a time would be worse than no count, since it is the number on the
   * button she presses.
   *
   * The words are deliberately not in the key: they change nothing about who or
   * what, and re-previewing on every keystroke would put a studio-wide query
   * behind a text field.
   */
  /*
   * <b>Settled before it is asked</b> (D-802). A native `<input type="time">`
   * raises `change` on <i>every</i> spinner click and every typed digit, so
   * picking 7:30 PM is four or five separate values - and each one was a fresh
   * studio-wide query. That is exactly what the paragraph above already
   * refuses for the note field; the time field was doing it unnoticed.
   */
  const settledTime = useSettled(fromTime)
  const settledFrom = useSettled(from)
  const settledTo = useSettled(to)

  /*
   * <b>And the scope is in the key</b> (D-868), because it changes the count.
   * *Cancel regularly scheduled classes only* leaves the privates and the
   * one-offs on, so the number on the button moves the moment she picks it -
   * and the button says *Close and tell 262 people*. It needs no settling
   * either: two options are one click, not a spinner firing five values.
   */
  const preview = useQuery({
    queryKey: ['closures', 'preview', settledFrom, settledTo, settledTime, regularScheduleOnly],
    queryFn: () =>
      previewClosure({
        from: settledFrom,
        to: settledTo,
        fromTime: settledTime || null,
        regularScheduleOnly,
      }),
    retry: false,

    /*
     * <b>Keep the last answer on screen while the next one loads</b> - the
     * idiom the calendar screens already use, and here it is the whole bug
     * fix. `observed`, reported from the studio: *"when clicking the times in
     * the close studio modal it seems to like refresh the modal and it jitters
     * and contracts and re-renders."*
     *
     * A new key has no cached data, so `isPending` was true on every click and
     * <b>the five-line cost block collapsed to one line</b> - *Working out what
     * this closes...* - while the button flipped from *Close and tell 354
     * people* to a disabled *Close the studio*. The sheet lost about four lines
     * of height and grew back a moment later, <b>underneath a native picker
     * popup anchored to the input</b>, which is what makes it jitter rather
     * than merely flicker.
     */
    placeholderData: (previous) => previous,
  })

  /*
   * <b>Stale counts may be shown, never acted on</b> (D-209).
   *
   * Holding the previous answer is what stops the sheet collapsing, and it
   * means that for a moment the button offers a number computed for the time
   * she just changed away from - and that number is the whole of what she is
   * agreeing to. So the block keeps its shape and the button waits.
   */
  const settling =
    preview.isFetching || fromTime !== settledTime || from !== settledFrom || to !== settledTo

  /*
   * <b>Seeing the message is a step on the way to the button</b> (D-805).
   *
   * `asked`, 2026-09-23: *"what if it was like a gate before you actually hit
   * close... you fill out the stuff, enter notes, click preview which shows
   * what the email would say, then clicking submit would actually send it
   * out? would that be cleaner and safer?"* Yes, and for a reason beyond
   * saving a request per keystroke: closing reaches four hundred families at
   * once and cannot be undone by pressing it again, so <i>look at what you are
   * about to send</i> earns its place on the path.
   *
   * <b>The approval is bound to the words it approved.</b> `previewedFor` holds
   * the exact message-affecting values the preview was rendered from, and the
   * gate reopens the moment any of them differ. A gate that can be satisfied
   * and then walked around is worse than none - she would have approved
   * wording that is not what goes out.
   *
   * <b>The studio's own `reason` is deliberately not in it.</b> It never
   * reaches a family - the template's `{{reason}}` is fed the family note
   * (D-793) - so changing it must not throw away an approval. That difference
   * is invisible on the screen otherwise, and this is a good place to teach it.
   */
  /*
   * <b>The scope is in here, and it was not until the message depended on it</b>
   * (D-868).
   *
   * While the option only changed *who* was told, leaving it out was right -
   * making her re-read an identical email is the step that teaches her the gate
   * is noise. Then the scope started choosing the **template**
   * (`closure.announced` against `closure.announced.regularOnly`), and the same
   * omission became the thing this gate exists to prevent: approve *"everything
   * is cancelled"*, go back, switch to regular-only, and the body she approved
   * is not the body that goes out. A gate that can be satisfied and then walked
   * around is worse than none.
   */
  const messageShape = JSON.stringify([from, to, fromTime, familyNote, regularScheduleOnly])
  const [previewedFor, setPreviewedFor] = useState<string | null>(null)

  const message = useMutation({
    mutationFn: () => previewClosureMessage(request),
    onSuccess: () => {
      setPreviewedFor(messageShape)
      setReviewing(true)
    },
  })

  const approved = previewedFor === messageShape && message.data !== undefined

  /*
   * <b>A step, not a section</b> (D-805, corrected after seeing it).
   *
   * The first cut appended the message to the bottom of the form. It was still
   * fetched once, on demand - it never updated as she typed - but a panel that
   * grows under a form <i>reads</i> as a live preview, which is the thing the
   * owner had already ruled out: <i>"i thought we weren't doing a live preview
   * at the bottom of the page, you were going to make that the
   * gate/confirmation."</i> He was right, and the fault was presentation rather
   * than behaviour.
   *
   * <b>It also made the sheet about two thousand pixels tall</b>, so the words
   * she is being asked to approve sat below the fold of the thing asking her.
   *
   * <b>Going back does not throw the approval away</b> - only editing does.
   * `approved` is keyed on the words themselves, so this is only which pane is
   * on screen.
   */
  const [reviewing, setReviewing] = useState(false)
  const onReview = reviewing && approved

  const close = useMutation({
    mutationFn: () => closeTheStudio(request),
    onSuccess: onClosed,
  })

  const counts = preview.data

  // Blank means the whole day, which is what the endpoint has always taken
  // (D-741). The toggle above names that rather than leaving it to an empty
  // field.
  const wholeDay = fromTime === ''

  return (
    <Modal
      onDismiss={close.isPending ? undefined : onDismiss}
      labelledBy="close-studio-heading"
      /*
        <b>`bg-surface`, as every other dialog in the app uses</b> (D-781).
        This said `bg-paper`, and there is no `paper` colour - it was the only
        use of the token in the SPA, so Tailwind emitted no rule and the panel
        rendered with **no background**: on QA the calendar showed straight
        through the form and it was unreadable. Typecheck and lint cannot see
        this, because an unknown utility class is valid TSX and valid CSS.
      */
      panelClassName="w-full max-w-lg rounded-2xl border border-line bg-surface p-5 shadow-xl"
    >
      <h2 id="close-studio-heading" className="font-display text-xl text-ink">
        Close the studio
      </h2>

      {/*
        <b>Follows the field, never the day she opened from</b> (D-803). This
        printed the calendar's day, which was fine while that was also the only
        day this could close - and became a contradiction the moment the first
        day became something she could change.
      */}
      <p className="mt-1 text-sm text-ink-2">
        {from === to ? formatFullDate(from) : `${formatFullDate(from)} to ${formatFullDate(to)}`}
      </p>

      <div className="mt-4 flex flex-col gap-4" hidden={onReview}>
        {/*
          <b>Two choices, not one field that means two things</b> (D-781). It
          was a bare time input labelled *"From a time, or leave blank for the
          whole day"* - so the ordinary case, a whole snow day, was expressed by
          touching nothing, and an empty field read as unanswered rather than as
          an answer.
        */}
        {/*
          <b>Two dates, side by side, the way every leave form does it</b>
          (D-803). One field labelled *Last day closed* with *leave it blank*
          underneath made the first day look fixed and the second look
          optional; neither read as an end of a range, and the start could not
          be moved at all.

          <b>`min` is today on the first and the first on the last</b>, so the
          browser refuses a past closure and an inverted range before the
          server has to - and the server still refuses both, because a picker
          is a convenience and never the boundary (D-73's reasoning, one screen
          over).
        */}
        <div className="flex flex-wrap gap-3">
          <label className="flex min-w-[9rem] flex-1 flex-col gap-1 text-sm">
            <span className="font-semibold text-ink-2">First day closed</span>
            <input
              id="close-studio-from"
              type="date"
              value={from}
              min={today()}
              onChange={(e) => setFirstDay(e.target.value)}
              className="w-full rounded-lg border border-line px-3 py-2"
            />
          </label>

          <label className="flex min-w-[9rem] flex-1 flex-col gap-1 text-sm">
            <span className="font-semibold text-ink-2">Last day closed</span>
            <input
              id="close-studio-to"
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              className="w-full rounded-lg border border-line px-3 py-2"
            />
          </label>
        </div>

        <span className="-mt-2 text-xs text-ink-3">
          {from === to
            ? 'One day. Change the last day to close a run of them.'
            : `${dayCount(from, to)} days, and the time below applies to each.`}
        </span>

        <div className="flex flex-col gap-1 text-sm">
          <span className="font-semibold text-ink-2">When</span>

          <div className="flex gap-2">
            {([
              ['All day', true],
              ['From a time', false],
            ] as const).map(([label, allDay]) => (
              <button
                key={label}
                type="button"
                aria-pressed={wholeDay === allDay}
                onClick={() => setFromTime(allDay ? '' : fromTime || DefaultCutoff)}
                className={`min-h-[2.25rem] rounded-lg border px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                  wholeDay === allDay
                    ? 'border-accent bg-accent-soft text-accent-2'
                    : 'border-line-2 bg-surface text-ink-2 hover:border-ink-3'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {!wholeDay && (
            <>
              <input
                id="close-studio-from-time"
                type="time"
                value={fromTime}
                onChange={(e) => setFromTime(e.target.value)}
                className="mt-1 w-full rounded-lg border border-line px-3 py-2"
              />
              {/*
                Half-open, and the sentence says so: a class starting exactly at
                the cutoff is off (D-81). Nobody reads a boundary rule off a
                time field.
              */}
              <span className="text-xs text-ink-3">
                Anything starting at that time or later is cancelled. Earlier classes run as normal.
              </span>
            </>
          )}
        </div>


        <label className="flex flex-col gap-1 text-sm">
          <span className="font-semibold text-ink-2">Reason</span>
          <input
            id="close-studio-reason"
            value={reason}
            maxLength={300}
            placeholder="Snow"
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-lg border border-line px-3 py-2"
          />
          <span className="text-xs text-ink-3">
            Shown on the studio's calendar. Families never see it.
          </span>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-semibold text-ink-2">Note to families</span>
          <textarea
            id="close-studio-family-note"
            value={familyNote}
            maxLength={500}
            rows={2}
            placeholder="The building is being used for the festival."
            onChange={(e) => setFamilyNote(e.target.value)}
            className="w-full rounded-lg border border-line px-3 py-2"
          />
          {/*
            Two reasons, deliberately (D-777): one is ours and one goes out under
            the studio's name to a hundred and sixty households. Blank is the
            ordinary case - a snow day explains itself and a festival does not.
          */}
          <span className="text-xs text-ink-3">
            Goes out in the message. Leave it blank to say nothing extra.
          </span>
        </label>

        {/*
          What the closure reaches (D-868).

          Most closures are weather, where everything comes off because nobody is
          reaching the building. The other case is the regular schedule being
          cancelled while teachers still run privates and file one-off classes
          that day - and nothing in the data can tell the two apart, so she says
          which this is.

          <b>Two named options rather than one tick box</b>, which is the owner's
          own framing: *"cancel regularly scheduled classes vs cancel all classes
          or something"*. A checkbox leaves its unchecked meaning implicit, and
          the implicit one here is the one that emails a hundred and sixty
          families - the shape D-204 keeps naming. Both states say what they do,
          and the first is still the default.
        */}
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold text-ink-2">
            What this cancels
          </legend>

          {([false, true] as const).map((only) => (
            <label
              key={String(only)}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 ${
                regularScheduleOnly === only ? 'border-accent' : 'border-line'
              }`}
            >
              <input
                type="radio"
                name="closure-scope"
                checked={regularScheduleOnly === only}
                onChange={() => setRegularScheduleOnly(only)}
                className="mt-1 accent-accent"
              />
              <span className="flex flex-col gap-1">
                <span className="text-[13.5px] font-semibold">
                  {only ? 'Cancel regularly scheduled classes only' : 'Cancel all classes'}
                </span>
                <span className="text-[11.5px] leading-relaxed text-ink-2">
                  {only
                    ? 'For a day like a festival. Teachers can still run privates and file one-off classes, and nobody is told those are off.'
                    : 'Everything on these days comes off - regular classes, one-off classes and privates.'}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
      </div>

      {/*
        <b>Still shown on the review step</b> (D-805), because the button beside
        it names the same number - *Close and tell 262 people* - and a count she
        cannot see beside it is a number she is asked to take on trust.
      */}
      <WhatItCosts query={preview} counts={counts} />

      {/*
        <b>The gate</b> (D-805). Shown once she asks for it, and taken away
        again the moment the words change - `approved` is bound to the exact
        values it was rendered from.
      */}
      {onReview && message.data && (
        <div className="mt-4 rounded-xl border border-line bg-surface-2 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-3">
            What families will get
          </p>

          <p className="mt-2 text-sm font-semibold text-ink">{message.data.subject}</p>

          {/*
            `whitespace-pre-wrap` because the body is written with real
            newlines in a textarea (A-16) and arrives as plain text - the
            dispatcher sends it as plain text too, so this is the shape of the
            actual email rather than a rendering of it.
          */}
          <p className="mt-2 whitespace-pre-wrap text-sm text-ink-2">{message.data.body}</p>

          <p className="mt-3 text-xs text-ink-3">
            Addressed to you here. Every family gets their own name.
          </p>

          {message.data.unresolved.length > 0 && (
            <p role="alert" className="mt-2 text-xs font-semibold text-warn">
              {message.data.unresolved.map((f) => `{{${f}}}`).join(', ')} will send exactly like
              that. Fix the wording in Messages before closing.
            </p>
          )}

          {!message.data.enabled && (
            <p role="alert" className="mt-2 text-xs font-semibold text-warn">
              This message is switched off, so closing will tell nobody.
            </p>
          )}
        </div>
      )}

      {message.isError && (
        <p role="alert" className="mt-3 text-sm text-warn">
          {(message.error as Error).message}
        </p>
      )}

      {close.isError && (
        <p role="alert" className="mt-3 text-sm text-warn">
          {(close.error as Error).message}
        </p>
      )}

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        {/*
          <b>Back on the review step, Cancel on the form</b> (D-805). Going
          back is the ordinary move from a confirmation - she reads the message
          and wants to change a word - and it must not be spelt *Cancel*, which
          throws the whole thing away.

          <b>Back keeps the approval</b>, because nothing she read has changed;
          only editing takes it back.
        */}
        <button
          type="button"
          onClick={onReview ? () => setReviewing(false) : onDismiss}
          disabled={close.isPending}
          className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2"
        >
          {onReview ? 'Back' : 'Cancel'}
        </button>

        {/*
          <b>The step before the step</b> (D-805). Until she has read the
          message, this is the only thing on the way forward - and it comes
          back the moment the wording changes underneath an approval.
        */}
        {!onReview && (
          <button
            type="button"
            onClick={() => (approved ? setReviewing(true) : message.mutate())}
            disabled={message.isPending || settling || preview.isPending || preview.isError}
            className="rounded-lg border border-line-2 px-4 py-2 text-sm font-semibold text-ink-2 disabled:opacity-60"
          >
            {message.isPending ? 'Working it out...' : 'Preview the email'}
          </button>
        )}

        <button
          type="button"
          onClick={() => close.mutate()}
          /*
            Disabled until the preview has answered, because the whole point is
            that she presses a button naming what it will do. A button that says
            "Close the studio" while the count is still loading is the one she
            would press by mistake.

            <b>And until she has read the message</b> (D-805): this reaches four
            hundred families at once and pressing it again does not undo it, so
            seeing the words is a step rather than an option.
          */
          disabled={
            close.isPending || !onReview || settling || preview.isPending || preview.isError
          }
          /*
            <b>The destructive-confirm pairing the app already uses</b>
            (`ConfirmAction`): a soft ground with the strong colour as text.
            A solid `bg-warn` cannot work in both themes - the token is dark
            amber in one and light amber in the other, so no single text colour
            contrasts against it. It carried `text-paper`, which does not
            exist.
          */
          className="rounded-lg border border-warn bg-warn-bg px-4 py-2 text-sm font-bold text-warn disabled:opacity-60"
        >
          {close.isPending
            ? 'Closing...'
            : counts
              ? `Close and tell ${willHear(counts)} ${willHear(counts) === 1 ? 'person' : 'people'}`
              : 'Close the studio'}
        </button>
      </div>
    </Modal>
  )
}

/**
 * What goes, and who hears.
 *
 * **A failed preview says so rather than showing nothing** (D-209). An empty
 * panel and *"this closes nothing"* look identical, and one of them is a reason
 * to press the button.
 */
function WhatItCosts({
  query,
  counts,
}: {
  query: { isPending: boolean; isError: boolean }
  counts: ClosurePreview | undefined
}) {
  if (query.isError) {
    return (
      <p role="alert" className="mt-4 rounded-xl bg-warn-bg px-4 py-3 text-sm text-warn">
        We could not work out what this would close. Try again before closing anything.
      </p>
    )
  }

  if (query.isPending || !counts) {
    return <p className="mt-4 text-sm text-ink-3">Working out what this closes...</p>
  }

  return (
    <div className="mt-4 rounded-xl bg-surface-2 px-4 py-3 text-sm text-ink-2">
      <p>
        <b className="text-ink">
          {counts.classes} {counts.classes === 1 ? 'class' : 'classes'}
        </b>{' '}
        and{' '}
        <b className="text-ink">
          {counts.privates} {counts.privates === 1 ? 'lesson' : 'lessons'}
        </b>{' '}
        cancelled.
      </p>

      {/*
        The number that stops the grid and the count disagreeing. A session
        already under way is deliberately left running (D-741) - it is what
        makes closing from 6pm safe to tap at 5:50 - so without this line she
        would count the blocks on screen and get a different answer.
      */}
      {counts.alreadyStarted > 0 && (
        <p className="mt-1">
          {counts.alreadyStarted} already under way{' '}
          {counts.alreadyStarted === 1 ? 'is' : 'are'} left running.
        </p>
      )}

      {/*
        <b>Told is who will actually hear</b> (D-781, and D-209's instinct
        applied to a count rather than a lookup). This read
        *"{people} told — … by email, … in the app only"* directly above
        *"{unreachable} … will not hear"*, and both cannot be true: `people` is
        the whole audience, and the unreachable are inside it. On QA it said
        **439 told** over **85 will not hear**, and the button offered to tell
        439 when it would reach 354. The fixture here used `unreachable: 1`,
        so for months the two numbers differed by one and nobody saw it.
      */}
      <p className="mt-1">
        {counts.unreachable > 0 ? (
          <>
            <b className="text-ink">{willHear(counts)}</b> of {counts.people} will hear
          </>
        ) : (
          <>
            All <b className="text-ink">{counts.people}</b> will hear
          </>
        )}{' '}
        — {counts.emailed} by email, {counts.inAppOnly} in the app only.
      </p>

      {counts.unreachable > 0 && (
        <p className="mt-1 text-warn">
          {counts.unreachable} we hold no address or login for, so they will not hear.
        </p>
      )}
    </div>
  )
}

/**
 * Late afternoon, because a cutoff is almost always "the evening is off".
 *
 * A default rather than a blank: tapping <i>From a time</i> and leaving the
 * field empty would send no cutoff at all, which is a whole-day closure
 * wearing the label of a partial one.
 */
const DefaultCutoff = '17:00'

/**
 * The people this will actually reach (D-781).
 *
 * <c>people</c> is the whole audience and <b>includes the unreachable</b>, so it
 * is the wrong number to put on a button that says <i>tell</i>. Everyone is
 * told at most once across every channel (D-736), so the two channels add up
 * without double-counting anybody.
 */
function willHear(counts: { emailed: number; inAppOnly: number }): number {
  return counts.emailed + counts.inAppOnly
}

/**
 * A value once it has stopped moving (D-802).
 *
 * **For a field that raises a change per digit.** A native `<input
 * type="time">` fires on every spinner click and every keystroke, so one
 * decision - *from half past seven* - arrives as four or five values, and the
 * query behind it is studio-wide.
 *
 * **250ms, which is below the threshold a person reads as lag** and well above
 * the gap between two clicks of the same spinner. The number is deliberately
 * not a setting: it is a property of how fast hands move, not of this studio.
 *
 * **Local to this file on purpose.** There is one caller. When a second
 * appears, hoist it rather than writing a second copy - two implementations of
 * one idea drift, and the second never receives the first's fixes.
 */
/**
 * How many days a closed range covers, inclusive (D-803).
 *
 * **UTC-anchored, so it is DST-proof.** Day arithmetic on something that is
 * already a date is pure counting; what must never come from UTC is *which
 * day it is* (D-68), and that is `today()`'s job, not this one.
 */
function dayCount(from: string, to: string): number {
  const day = 24 * 60 * 60 * 1000
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / day) + 1
}

function useSettled<T>(value: T, ms = 250): T {
  const [settled, setSettled] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])

  return settled
}
